import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import * as d3 from 'd3';
import { DataSet, ColumnId, ReferenceLine, ContourLine } from '../../types';
import type { DensityDisplayMode, DensityVisualization, DensityBandwidth, DensityColorScale } from '../../types/graph-builder.types';
import { COLORS } from '../constants/colors';
import { useD3ScatterPlot } from '../hooks/useD3ScatterPlot';
import { useD3Zoom } from '../hooks/useD3Zoom';
import { useD3Selection } from '../hooks/useD3Selection';
import { useLazyVisualization } from '../hooks/useLazyVisualization';
import { ClearSelectionButton } from './common/ClearSelectionButton';
import { LazyVisualizationPlaceholder } from './common/LazyVisualizationPlaceholder';

interface ScatterPlotProps {
  dataset: DataSet;
  xColumn: ColumnId;
  yColumn: ColumnId;
  colorColumn?: ColumnId;
  filteredRowIndices?: number[] | null;
  selectedRows?: number[];
  width?: number;
  height?: number;
  onPointClick?: (dataIndices: number[], isAdditive?: boolean) => void;
  referenceLines?: ReferenceLine[];
  contourLines?: ContourLine[];
  xDomain?: [number, number];  // 初期X軸表示範囲（指定しない場合はデータから自動計算）
  yDomain?: [number, number];  // 初期Y軸表示範囲（指定しない場合はデータから自動計算）
  hideControls?: boolean;  // UIコントロール非表示（ズーム、選択モードボタンなど）
  compact?: boolean;  // コンパクト表示（軸ラベル非表示）

  // 密度可視化の設定
  displayMode?: DensityDisplayMode;
  densityVisualization?: DensityVisualization;
  densityBandwidth?: DensityBandwidth;
  contourLevels?: number;
  densityColorScale?: DensityColorScale;
}

export function ScatterPlot({
  dataset,
  xColumn,
  yColumn,
  colorColumn,
  filteredRowIndices,
  selectedRows = [],
  width,
  height,
  onPointClick,
  referenceLines,
  contourLines,
  xDomain,
  yDomain,
  hideControls = false,
  compact = false,
  displayMode,
  densityVisualization,
  densityBandwidth,
  contourLevels,
  densityColorScale
}: ScatterPlotProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // モード管理（pan or select）
  const [mode, setMode] = useState<'pan' | 'select'>('pan');

  // 実際のコンテナサイズの状態管理
  const [actualWidth, setActualWidth] = useState<number | null>(null);
  const [actualHeight, setActualHeight] = useState<number | null>(null);

  // データサイズの計算（遅延レンダリングの判定に使用）
  const dataSize = useMemo(() => {
    return filteredRowIndices?.length || dataset.rowCount || 0;
  }, [dataset.rowCount, filteredRowIndices]);

  // 遅延可視化フック（1,000行以上の場合はプレースホルダー表示）
  const {
    shouldRender,
    requestRender,
    isLoading,
    error,
    retry,
    notifyRenderComplete
  } = useLazyVisualization({
    initialRender: dataSize < 1000
  });
  
  // 現在のズームトランスフォームを保持
  const [currentTransform, setCurrentTransform] = useState<d3.ZoomTransform>(d3.zoomIdentity);

  // ブラシの状態を保持
  const brushRef = useRef<d3.BrushBehavior<unknown> | null>(null);
  const brushGroupRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null);
  const isClearingRef = useRef(false);

  // カスタムフックを使用
  const {
    validPoints,
    xScale,
    yScale,
    innerWidth,
    innerHeight,
    margin,
    drawPoints,
    updateAxes,
    createBrushGroup,
    drawReferenceLines,
    drawContourLines,
    drawDensityContours
  } = useD3ScatterPlot(
    svgRef,
    dataset,
    xColumn,
    yColumn,
    actualWidth ?? 0, // actualWidthがnullの時は0を渡す（初期化をトリガーするため）
    actualHeight ?? height ?? 200, // actualHeightがnullの時はpropsのheight、それもなければ200を使用
    filteredRowIndices,
    colorColumn,
    {
      xDomain,
      yDomain,
      compact,
      displayMode,
      densityVisualization,
      densityBandwidth,
      contourLevels,
      densityColorScale
    }
  );

  // 選択状態を管理するフック
  const selection = useD3Selection(selectedRows);

  // ズーム機能のフック（modeに応じてフィルターを変更）
  const zoomFilter = useCallback((event: WheelEvent | MouseEvent | TouchEvent) => {
    if (mode === 'pan') {
      // パンモード：ホイールはCtrl/Cmd、ドラッグは許可
      if (event.type === 'wheel') {
        return (event as WheelEvent).ctrlKey || (event as WheelEvent).metaKey;
      }
      // マウスダウンとマウスムーブイベントは許可（ドラッグパン用）
      if (event.type === 'mousedown' || event.type === 'mousemove') {
        return !(event as MouseEvent).button; // 左クリックのみ
      }
      // タッチイベントも許可
      return true;
    } else {
      // 選択モード：ホイールのみ（Ctrl/Cmd必須）
      return event.type === 'wheel' && ((event as WheelEvent).ctrlKey || (event as WheelEvent).metaKey);
    }
  }, [mode]);

  // ズーム時の処理
  const handleZoom = useCallback((transform: d3.ZoomTransform) => {
    // トランスフォームを保存
    setCurrentTransform(transform);

    // トランスフォームを適用した新しいスケールを作成
    const visibleXScale = transform.rescaleX(xScale);
    const visibleYScale = transform.rescaleY(yScale);

    // 軸を更新
    updateAxes(visibleXScale, visibleYScale);

    // 密度等高線を再描画
    drawDensityContours(visibleXScale, visibleYScale);

    // 参照線を再描画
    if (referenceLines) {
      drawReferenceLines(referenceLines, visibleXScale, visibleYScale);
    }

    // 等高線を再描画
    if (contourLines) {
      drawContourLines(contourLines, visibleXScale, visibleYScale);
    }

    // ポイントを再描画
    const points = drawPoints(visibleXScale, visibleYScale, selectedRows, onPointClick);

    // 選択状態のスタイルを適用
    if (points) {
      points
        .style('stroke', d => selection.getStroke(d.index))
        .style('stroke-width', d => selection.getStrokeWidth(d.index))
        .style('opacity', d => selection.getOpacity(d.index))
        .attr('r', d => selection.getSize(d.index));
    }
  }, [xScale, yScale, updateAxes, drawPoints, drawReferenceLines, drawContourLines, drawDensityContours, selectedRows, onPointClick, selection, referenceLines, contourLines]);

  // ズームフックを使用
  useD3Zoom(
    svgRef,
    handleZoom,
    {
      scaleExtent: [0.1, 10],
      filter: zoomFilter,
      enableDoubleClickReset: true
    }
  );

  // コンテナサイズの変更を監視
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const containerWidth = entry.contentRect.width;
        const containerHeight = entry.contentRect.height;
        // ResizeObserverが報告した実際のコンテナサイズを設定
        setActualWidth(containerWidth);
        setActualHeight(containerHeight);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [width, height]);

  // 初期描画とデータ更新時の処理
  useEffect(() => {
    // 遅延レンダリング：shouldRenderがfalseの場合は描画をスキップ
    if (!shouldRender) return;
    if (!xScale || !yScale) return;

    // 現在のトランスフォームを適用したスケールを作成
    const visibleXScale = currentTransform.rescaleX(xScale);
    const visibleYScale = currentTransform.rescaleY(yScale);

    // 軸を更新
    updateAxes(visibleXScale, visibleYScale);

    // 密度等高線を描画（最初に描画される）
    drawDensityContours(visibleXScale, visibleYScale);

    // 参照線を描画（ポイントより先に描画される）
    if (referenceLines) {
      drawReferenceLines(referenceLines, visibleXScale, visibleYScale);
    }

    // 等高線を描画（参照線の後、ポイントより先に描画される）
    if (contourLines) {
      drawContourLines(contourLines, visibleXScale, visibleYScale);
    }

    // ポイントを描画
    const points = drawPoints(visibleXScale, visibleYScale, selectedRows, onPointClick);

    // 選択状態のスタイルを適用
    if (points) {
      points
        .style('stroke', d => selection.getStroke(d.index))
        .style('stroke-width', d => selection.getStrokeWidth(d.index))
        .style('opacity', d => selection.getOpacity(d.index))
        .attr('r', d => selection.getSize(d.index));
    }

    // レンダリング完了を通知
    notifyRenderComplete();
  }, [shouldRender, xScale, yScale, updateAxes, drawPoints, drawReferenceLines, drawContourLines, drawDensityContours, selectedRows, onPointClick, selection, validPoints, currentTransform, referenceLines, contourLines, notifyRenderComplete]);

  // モード切り替え時のブラシ設定
  useEffect(() => {
    if (!svgRef.current || mode !== 'select') {
      // パンモードまたはSVGがない場合はブラシをクリア
      if (brushGroupRef.current) {
        brushGroupRef.current.selectAll('*').remove();
        brushGroupRef.current = null;
        brushRef.current = null;
      }
      return;
    }

    // 選択モード：ブラシを設定
    const brushGroup = createBrushGroup();
    if (!brushGroup) return;
    
    brushGroupRef.current = brushGroup;

    const brush = d3.brush()
      .extent([[margin.left, margin.top], [margin.left + innerWidth, margin.top + innerHeight]])
      .filter((event: MouseEvent) => {
        return !event.ctrlKey && !event.metaKey;
      })
      .on('start brush end', (event) => {
        
        // クリア中は無視
        if (isClearingRef.current) return;
        
        if (event.type === 'end' && event.selection) {
          const [[x0, y0], [x1, y1]] = event.selection;
          
          // マージンを考慮して座標を調整
          const adjustedX0 = x0 - margin.left;
          const adjustedY0 = y0 - margin.top;
          const adjustedX1 = x1 - margin.left;
          const adjustedY1 = y1 - margin.top;
          
          // 現在のトランスフォームを使用
          const visibleXScale = currentTransform.rescaleX(xScale);
          const visibleYScale = currentTransform.rescaleY(yScale);
          
          // 選択範囲内の点を見つける
          const selectedIndices = validPoints
            .filter(point => {
              const px = visibleXScale(point.x);
              const py = visibleYScale(point.y);
              return px >= adjustedX0 && px <= adjustedX1 && 
                     py >= adjustedY0 && py <= adjustedY1;
            })
            .map(point => point.index);
          
          
          if (onPointClick && selectedIndices.length > 0) {
            onPointClick(selectedIndices, false);
          }
          
          // ブラシをクリア
          isClearingRef.current = true;
          brushGroup.call(brush.move, null);
          isClearingRef.current = false;
        }
      });

    brushRef.current = brush;
    brushGroup.call(brush);
    
    // 選択矩形のスタイル
    brushGroup.select('.selection')
      .style('fill', COLORS.primary.blue)
      .style('fill-opacity', 0.2)
      .style('stroke', COLORS.primary.blue)
      .style('stroke-width', 1);

    return () => {
      // クリーンアップ
      if (brushGroupRef.current) {
        brushGroupRef.current.selectAll('*').remove();
      }
    };
  }, [mode, margin, innerWidth, innerHeight, xScale, yScale, validPoints, onPointClick, createBrushGroup, currentTransform]);

  // actualWidthがnullの場合は初期化待ち
  const showChart = shouldRender && !error && actualWidth !== null;

  // 使用する高さを決定: actualHeightがあればそれを使用、なければpropsのheight、それもなければ200
  const effectiveHeight = actualHeight ?? height ?? 200;

  return (
    <div ref={containerRef} style={{ width: '100%', height: height ?? '100%', overflow: 'hidden' }}>
      {/* 遅延レンダリング：プレースホルダー表示 */}
      {(!shouldRender || error) && (
        <LazyVisualizationPlaceholder
          dataSize={dataSize}
          onView={requestRender}
          visualizationType="scatter plot"
          height={effectiveHeight}
          isLoading={isLoading}
          error={error}
          onRetry={retry}
        />
      )}

      {/* SVGチャート：常にDOMに存在させ、display制御で表示/非表示 */}
      <div style={{ position: 'relative', display: showChart ? 'block' : 'none' }}>
        {/* SVGチャート */}
        <svg
          ref={svgRef}
          width={actualWidth ?? 0}
          height={effectiveHeight}
          style={{
            border: '1px solid #e9ecef',
            borderRadius: '4px',
            background: 'white',
            cursor: mode === 'pan' ? 'grab' : 'crosshair',
            display: 'block',
            maxWidth: '100%'
          }}
        />

        {/* オーバーレイボタン */}
        {!hideControls && (
        <div style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          display: 'flex',
          gap: '4px',
          background: 'rgba(255, 255, 255, 0.9)',
          borderRadius: '4px',
          padding: '2px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <button
            onClick={() => setMode('pan')}
            title="Pan mode"
            style={{
              width: '28px',
              height: '28px',
              padding: '0',
              border: 'none',
              borderRadius: '4px',
              background: mode === 'pan' ? 'rgba(66, 133, 244, 0.2)' : 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (mode !== 'pan') {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (mode !== 'pan') {
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            {/* パンアイコン（移動矢印） */}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 1L6 3h1.5v4.5H3V6L1 8l2 2v-1.5h4.5V13H6l2 2 2-2H8.5V8.5H13V10l2-2-2-2v1.5H8.5V3H10L8 1z" 
                fill={mode === 'pan' ? COLORS.primary.blue : COLORS.background.mutedText} />
            </svg>
          </button>
          <button
            onClick={() => setMode('select')}
            title="Selection mode"
            style={{
              width: '28px',
              height: '28px',
              padding: '0',
              border: 'none',
              borderRadius: '4px',
              background: mode === 'select' ? 'rgba(66, 133, 244, 0.2)' : 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (mode !== 'select') {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (mode !== 'select') {
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            {/* 選択アイコン（矩形選択） */}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="2" y="2" width="12" height="12" rx="1" 
                stroke={mode === 'select' ? COLORS.primary.blue : COLORS.background.mutedText} 
                strokeWidth="1.5" 
                strokeDasharray="3 2"
                fill="none" />
            </svg>
          </button>
          <ClearSelectionButton
            onClear={() => onPointClick?.([], false)}
            hasSelection={selectedRows.length > 0}
            style={{
              marginLeft: '4px',
              borderLeft: '1px solid rgba(0, 0, 0, 0.1)'
            }}
          />
        </div>
        )}
      </div>
    </div>
  );
}